import { RelayType } from '@diritto/common/content/constants';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import styled from '@emotion/styled';
import { useAuthState } from 'contexts/AuthProvider';
import { ProfileContentItem } from 'app-components/accounts/profiles/ProfileContentItem';
import { useGetRelayProfileContentsInfiniteQuery } from 'queries/contents/useGetRelayProfileContentsInfiniteQuery';
import React, { useMemo } from 'react';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { Loading } from 'ui-components/common/Loading';

interface ProfileContentListProps {
  profileId: number;
}

export const ProfileContentList = ({ profileId }: ProfileContentListProps) => {
  const { includeAdult } = useAuthState();

  const {
    data: contentsData,
    hasNextPage,
    fetchNextPage,
    isInitialLoading,
  } = useGetRelayProfileContentsInfiniteQuery(profileId, { includeAdult });

  const { contentIds, contents, profiles } = useMemo(
    () => ({
      contentIds: flatItems<number>('contentIds', contentsData?.pages) ?? [],
      contents: accurateItems<ContentRelayDto>('contents', contentsData?.pages),
      profiles: accurateItems<ProfileLiteRelayDto>('profileLites', contentsData?.pages) ?? {},
    }),
    [contentsData?.pages],
  );

  return (
    <ContentBodyPaddingContainer>
      <InfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
        {!isInitialLoading && contentIds.length === 0 && <EmptyList description="작품이 없어요." />}
        <StyledList>
          {contentIds.map(id => {
            const content = contents[id];
            if (content.relayType !== RelayType.Public) {
              return null;
            }
            const author = content.profileId && profiles[content.profileId];
            if (!author || author.isDeleted) {
              return null;
            }

            return <ProfileContentItem key={id} content={content} profile={author} />;
          })}
          {isInitialLoading && <Loading />}
        </StyledList>
      </InfiniteScrollWrapper>
    </ContentBodyPaddingContainer>
  );
};

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 8px;
`;
